/**
 * Position Previews Hook
 *
 * Simplified hook that uses CanvasStateManager as Single Source of Truth.
 * Previews are now generated and cached in the CanvasStateManager store.
 *
 * This hook provides a convenient API for accessing previews.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  useCanvasStateManager,
  usePositions,
  useActivePosition,
} from '@/stores/canvasStateManager';
import { isSystemLayer } from '@/types/canvas';
import { logger } from '@/utils/logger';

// Stable selectors to prevent unnecessary re-renders
const selectCanvas = (
  state: ReturnType<typeof useCanvasStateManager.getState>
) => state.canvas;
const selectUpdatePreview = (
  state: ReturnType<typeof useCanvasStateManager.getState>
) => state.updatePreview;

/**
 * Position preview data structure
 */
export interface PositionPreviews {
  /** Preview URLs keyed by position (slug or productPositionId) */
  [positionKey: string]: string | null;
}

/**
 * Hook to access preview images for each canvas position
 *
 * - Uses CanvasStateManager as the source of truth
 * - Auto-updates previews on canvas changes
 * - Provides API for accessing previews in ProductCards
 */
export function usePositionPreviews() {
  // Get stable positions reference
  const positions = usePositions();
  const activePosition = useActivePosition();
  // Use stable selectors to prevent infinite re-render loops
  const canvas = useCanvasStateManager(selectCanvas);
  const updatePreview = useCanvasStateManager(selectUpdatePreview);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitializedRef = useRef(false);

  // Derive previews using useMemo to prevent infinite loops
  // This is the correct pattern for derived data in Zustand
  const previews = useMemo(() => {
    const result: Record<string, string | null> = {};
    for (const [key, val] of Object.entries(positions)) {
      result[key] = val.preview;
    }
    return result;
  }, [positions]);

  /**
   * Debounced preview update
   */
  const debouncedUpdatePreview = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      updatePreview(activePosition);
    }, 500); // 500ms debounce (cards should stay smooth; updates happen on modified, not during drag)
  }, [activePosition, updatePreview]);

  /**
   * Subscribe to canvas changes for live preview updates
   */
  useEffect(() => {
    if (!canvas) return;

    // Generate initial preview only once to prevent infinite loops.
    // CRITICAL: Only call updatePreview if canvas actually has user objects.
    // Otherwise we overwrite a valid stored preview with null (race condition
    // when CanvasEditor remounts and objects haven't been loaded yet).
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      const hasUserObjects = canvas
        .getObjects()
        .some((obj) => !isSystemLayer(obj.name));
      if (hasUserObjects) {
        updatePreview(activePosition);
      }
    }

    // Listen for canvas changes
    const handleCanvasChange = () => {
      debouncedUpdatePreview();
    };

    // Subscribe to relevant canvas events.
    // Important: avoid high-frequency events like moving/scaling/rotating to keep ProductCards smooth.
    canvas.on('object:added', handleCanvasChange);
    canvas.on('object:removed', handleCanvasChange);
    canvas.on('object:modified', handleCanvasChange);

    return () => {
      // Cleanup event listeners
      canvas.off('object:added', handleCanvasChange);
      canvas.off('object:removed', handleCanvasChange);
      canvas.off('object:modified', handleCanvasChange);

      // Clear debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [canvas, activePosition, updatePreview, debouncedUpdatePreview]);

  /**
   * Get preview for a specific position
   */
  const getPreviewForPosition = useCallback(
    (position: string): string | null => {
      return previews[position] || null;
    },
    [previews]
  );

  /**
   * Clear all cached previews
   */
  const clearPreviews = useCallback(() => {
    logger.debug('PositionPreviews', 'Clearing all previews');
    // Note: This would need a clearAllPreviews action in CanvasStateManager
    // For now, previews are cleared via store reset
  }, []);

  return {
    /** All position previews */
    previews,
    /** Current active position */
    activePosition,
    /** Whether a preview is currently being generated - always false now */
    isGenerating: false,
    /** Get preview for a specific position */
    getPreviewForPosition,
    /** Manually regenerate preview for active position */
    regenerate: () => updatePreview(activePosition),
    /** Clear all cached previews */
    clearPreviews,
  };
}
